import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * PageBanner — reusable hero / page banner
 *
 * Props:
 *  bgImage     {string}   — imported image src used as the full-width background
 *  eyebrow     {string}   — small uppercase label above the title  (optional)
 *  title       {string}   — main heading (rendered as <h1>)
 *  breadcrumbs {Array}    — array of { label, to } objects
 *                           the last item is treated as the active/current page
 *  height      {string}   — Tailwind height class, default 'h-[52vh] min-h-[320px]'
 */
const PageBanner = ({
  bgImage,
  eyebrow = '',
  subtitle = '',
  title,
  breadcrumbs = [],
  height = 'h-[52vh] min-h-[320px]',
}) => {
  const displayEyebrow = eyebrow || subtitle;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      className={`relative ${height} flex items-center justify-center overflow-hidden`}
    >
      {/* ── Background ── */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt={title}
          className="w-full h-full object-cover object-center"
        />
        {/* Uniform dark overlay */}
        <div className="absolute inset-0 bg-[#0a1a0f]/60" />
        {/* Top + bottom vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a0f]/80 via-transparent to-[#0a1a0f]/40" />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 text-center px-6">
        {/* Eyebrow */}
        {displayEyebrow && (
          <p
            className={`text-white/50 text-[11px] tracking-[0.35em] uppercase mb-4 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
          >
            {displayEyebrow}
          </p>
        )}

        {/* Title */}
        <h1
          className={`text-white font-light text-5xl sm:text-6xl tracking-wide mb-5 transition-all duration-700 delay-150 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
        >
          {title}
        </h1>

        {/* Breadcrumb */}
        {breadcrumbs.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            className={`flex items-center justify-center gap-2 text-[11px] tracking-[0.25em] uppercase transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
          >
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <React.Fragment key={crumb.label}>
                  {/* Separator — shown before every item except the first */}
                  {i > 0 && (
                    <span className="text-amber-400 text-[8px]">●</span>
                  )}
                  {isLast ? (
                    <span className="text-white">{crumb.label}</span>
                  ) : (
                    <Link
                      to={crumb.to}
                      className="text-white/60 hover:text-white transition-colors duration-200"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        )}
      </div>
    </section>
  );
};

export default PageBanner;
